import { Trans, useTranslation } from 'react-i18next';
import { memo } from 'react';

import { GetEventDemographicsStatsQuery as GetEventDemographicsStats } from '__generated__/graphql';

import { FilterIndicator } from './Layout';
import { assetUrl, getUrlWithoutPrefix } from '../../common/helpers';
import OriginList from './OriginList';
import UI from '../../common/UI';
import useProject from '../useProject';

type Event = GetEventDemographicsStats['event'];

export interface TrackingStatsProps {
  event: Event;
}

const TrackingStats = memo(({ event } : TrackingStatsProps) => {
  const { analytics: { tracking } } = event;
  const { t } = useTranslation();
  const project = useProject();

  const utmTagsAvailable = (
    tracking.utm_source.length > 1
    || tracking.utm_medium.length > 1
    || tracking.utm_campaign.length > 1
    || !!tracking.utm_source[0]?.value
    || !!tracking.utm_medium[0]?.value
    || !!tracking.utm_campaign[0]?.value
  );
  const trackingAvailable = (
    utmTagsAvailable
    || tracking.initial_url.length > 1
    || tracking.final_url.length > 1
    || !!tracking.initial_url[0]?.value
    || !!tracking.final_url[0]?.value
  );

  if (!trackingAvailable) {
    return (
      <UI.Card>
        <UI.FlexContainer sc={{ alignItems: 'center', mb: 1 }}>
          <UI.H4>
            {t('origin_of_web_traffic')}
          </UI.H4>
          <FilterIndicator sc={{ ml: 2 }} />
        </UI.FlexContainer>
        <UI.Div sc={{ color: 'gray.500', mb: 3 }}>
          <Trans i18nKey="no_tracking_stats_description">
            <UI.Link to={`/${project.id}/events/${event.id}/settings?modal=embed`} />
          </Trans>
        </UI.Div>
        <UI.Div>
          <UI.FlexContainer sc={{ justifyContent: 'center', mb: 2 }}>
            <img
              src={assetUrl('images/dashboard-registrations.svg')}
              height={100}
              alt={t('origin_of_web_traffic')}
            />
          </UI.FlexContainer>
          <UI.Div sc={{ textAlign: 'center', color: 'gray.400' }}>
            {t('no_data_available')}
          </UI.Div>
        </UI.Div>
      </UI.Card>
    );
  }

  return (
    <>
      <UI.GridContainer sc={{ columns: ['1fr', '1fr 1fr'] }}>
        <OriginList
          title={t('tracking_landing_page')}
          description={t('tracking_landing_page_description')}
          items={event.analytics.tracking.initial_url}
          itemsCount={event.analytics.tracking.initial_url.length}
          totalCount={event.registrations_count}
          renderItem={(item) => (
            item.value ? (
              <UI.A href={item.value} target="_blank" rel="noopener noreferrer" sc={{ secondary: true }}>
                {getUrlWithoutPrefix(item.value)}
              </UI.A>
            ) : (
              <UI.Span sc={{ muted: true }}>{t('unknown')}</UI.Span>
            )
          )}
        />
        <OriginList
          title={t('tracking_registration_page')}
          description={t('tracking_registration_page_description')}
          items={event.analytics.tracking.final_url}
          itemsCount={event.analytics.tracking.final_url.length}
          totalCount={event.registrations_count}
          renderItem={(item) => (
            item.value ? (
              <UI.A href={item.value} target="_blank" rel="noopener noreferrer" sc={{ secondary: true }}>
                {getUrlWithoutPrefix(item.value)}
              </UI.A>
            ) : (
              <UI.Span sc={{ muted: true }}>{t('unknown')}</UI.Span>
            )
          )}
        />
      </UI.GridContainer>
      {utmTagsAvailable && (
        <UI.GridContainer sc={{ columns: ['1fr', '1fr 1fr 1fr'] }}>
          <OriginList
            title={t('utm_source')}
            items={event.analytics.tracking.utm_source}
            itemsCount={event.analytics.tracking.utm_source.length}
            totalCount={event.registrations_count}
            renderItem={(item) => (
              <>{item.value || <UI.Span sc={{ muted: true }}>{t('unknown')}</UI.Span>}</>
            )}
          />
          <OriginList
            title={t('utm_medium')}
            items={event.analytics.tracking.utm_medium}
            itemsCount={event.analytics.tracking.utm_medium.length}
            totalCount={event.registrations_count}
            renderItem={(item) => (
              <>{item.value || <UI.Span sc={{ muted: true }}>{t('unknown')}</UI.Span>}</>
            )}
          />
          <OriginList
            title={t('utm_campaign')}
            items={event.analytics.tracking.utm_campaign}
            itemsCount={event.analytics.tracking.utm_campaign.length}
            totalCount={event.registrations_count}
            renderItem={(item) => (
              <>{item.value || <UI.Span sc={{ muted: true }}>{t('unknown')}</UI.Span>}</>
            )}
          />
        </UI.GridContainer>
      )}
      <UI.Div sc={{ color: 'gray.500' }}>
        {t('tracking_stats_footnote')}
      </UI.Div>
    </>
  );
});

export default TrackingStats;
